import { BehaviorSubject, interval, merge, Observable } from 'rxjs';
import {
  BookableOfferingAppointment,
  BookingState,
  OfferingAppointment,
  OfferingBooking,
  OfferingBookingGuestDto,
  OfferingFilters,
  OfferingPricing,
  SelectedSalesListOfferingItem,
  WaitListEntry,
} from './models';
import { catchError, filter, startWith, switchMap, tap } from 'rxjs/operators';
import { OfferingService } from './offering.service';
import { Injectable } from '@angular/core';
import { isAfter, isFuture, isPast, parseISO } from 'date-fns';
import { DataResult, mapDataResult$ } from '../http/httpDataResponse';
import { HttpClient } from '@angular/common/http';
import { HttpErrorHandler } from '../http/httpErrorHandler.service';
import { ConfigService } from '../config/config.service';
import { ICustomerAppsConfig } from '../config/config.module';
import { OfferingFilterService } from './offering-filter.service';
import { ApiDateConverter } from '../http/api-date-converter.service';
import { ActivatedRoute } from '@angular/router';
import { AppStateService } from '../app-state/app-state.service';
import { SepaMandate } from '../accounting/models';

@Injectable({
  providedIn: 'root',
})
export class BookableOfferingService<T extends BookableOfferingAppointment> extends OfferingService<T> {
  constructor(
    httpClient: HttpClient,
    httpErrorHandler: HttpErrorHandler,
    configService: ConfigService<ICustomerAppsConfig>,
    offeringFilterService: OfferingFilterService,
    apiDateConverter: ApiDateConverter,
    route: ActivatedRoute,
    private appStateService: AppStateService,
  ) {
    super(httpClient, httpErrorHandler, configService, offeringFilterService, apiDateConverter, route);

    merge(this.appStateService.show, interval(180000))
      .pipe(filter(() => this.appStateService.isVisible.value === true))
      .subscribe(() => this.refreshWaitListEntries$.next());
  }

  private refreshWaitListEntries$ = new BehaviorSubject<void>(null);

  bookAppointment(
    appointment: BookableOfferingAppointment,
    memberId: number,
    invoiceMemberId: number,
    invoiceSepa?: SepaMandate,
    comment?: string,
    dogId?: string,
    clubExamIds?: string[],
    selectedSalesListOfferingItems?: SelectedSalesListOfferingItem[],
  ): Observable<BookableOfferingAppointment> {
    return this.httpClient
      .post<DataResult<BookableOfferingAppointment>>(`${this.configService.config.apiUrl}/offering-bookings`, {
        appointmentSeriesId: appointment.appointmentSeries.id,
        start: appointment.start,
        memberId,
        invoiceMemberId,
        invoiceSepa,
        comment,
        dogId,
        clubExamIds,
        selectedSalesListOfferingItems,
      })
      .pipe(catchError(this.httpErrorHandler.handleErrorCallback), mapDataResult$<BookableOfferingAppointment>());
  }

  private refreshWaitListEntries() {
    this.refreshWaitListEntries$.next();
  }

  addToAppointmentWaitList(appointment: BookableOfferingAppointment, memberId: number): Observable<BookableOfferingAppointment> {
    return this.httpClient
      .post<DataResult<BookableOfferingAppointment>>(`${this.configService.config.apiUrl}/offering-waitlist`, {
        appointmentSeriesId: appointment.appointmentSeries.id,
        start: appointment.start,
        memberId,
      })
      .pipe(catchError(this.httpErrorHandler.handleErrorCallback), mapDataResult$<BookableOfferingAppointment>());
  }

  createGuestAndBookAppointmentForGuest(
    appointment: BookableOfferingAppointment,
    guest: OfferingBookingGuestDto,
    invoiceMemberId: number,
    invoiceSepa?: SepaMandate,
    comment?: string,
    dogId?: string,
    clubExamIds?: string[],
    selectedSalesListOfferingItems?: SelectedSalesListOfferingItem[],
  ): Observable<BookableOfferingAppointment> {
    return this.httpClient
      .post<DataResult<BookableOfferingAppointment>>(`${this.configService.config.apiUrl}/offering-booking-with-child-data`, {
        appointmentSeriesId: appointment.appointmentSeries.id,
        start: appointment.start,
        invoiceMemberId,
        invoiceSepa,
        comment,
        dogId,
        clubExamIds,
        selectedSalesListOfferingItems,
        ...guest,
      })
      .pipe(catchError(this.httpErrorHandler.handleErrorCallback), mapDataResult$<BookableOfferingAppointment>());
  }

  createGuestAndAddToAppointmentWaitList(
    appointment: BookableOfferingAppointment,
    guest: OfferingBookingGuestDto,
  ): Observable<BookableOfferingAppointment> {
    return this.httpClient
      .post<DataResult<BookableOfferingAppointment>>(`${this.configService.config.apiUrl}/offering-waitlist-with-child-data`, {
        appointmentSeriesId: appointment.appointmentSeries.id,
        start: appointment.start,
        ...guest,
      })
      .pipe(catchError(this.httpErrorHandler.handleErrorCallback), mapDataResult$<BookableOfferingAppointment>());
  }

  cancelBooking(bookingId: string): Observable<BookableOfferingAppointment> {
    return this.httpClient
      .delete<DataResult<BookableOfferingAppointment>>(`${this.configService.config.apiUrl}/offering-bookings/${bookingId}`)
      .pipe(
        catchError(this.httpErrorHandler.handleErrorCallback),
        mapDataResult$<BookableOfferingAppointment>(),
        tap(() => this.refreshWaitListEntries()),
      );
  }

  cancelWaitListEntry(waitListEntryId: string): Observable<BookableOfferingAppointment> {
    return this.httpClient
      .delete<DataResult<BookableOfferingAppointment>>(`${this.configService.config.apiUrl}/offering-waitlist/${waitListEntryId}`)
      .pipe(
        catchError(this.httpErrorHandler.handleErrorCallback),
        mapDataResult$<BookableOfferingAppointment>(),
        tap(() => this.refreshWaitListEntries()),
      );
  }

  waitListEntryIsCurrentlyBookable(waitListEntry: WaitListEntry): boolean {
    return isFuture(parseISO(waitListEntry.reservedUntil));
  }

  appointmentHasStarted(appointment: OfferingAppointment): boolean {
    return isPast(parseISO(appointment.start));
  }

  bookablePeriodIsActive(appointment: OfferingAppointment): boolean {
    return this.bookablePeriodHasStarted(appointment) && !this.bookablePeriodHasEnded(appointment);
  }

  bookablePeriodHasEnded(appointment: OfferingAppointment): boolean {
    if (appointment.bookingEnd) {
      return isAfter(new Date(), this.apiDateConverter.apiDateToDate(appointment.bookingEnd));
    } else {
      return false;
    }
  }

  bookablePeriodHasStarted(appointment: OfferingAppointment): boolean {
    if (appointment.bookingStart) {
      return isAfter(new Date(), this.apiDateConverter.apiDateToDate(appointment.bookingStart));
    } else {
      return true;
    }
  }

  getBookableBookingsForAppointment(appointment: BookableOfferingAppointment): OfferingBooking[] {
    return appointment.bookings.filter((booking) => !booking.createdAt || booking.deletedAt);
  }

  getActiveBookingsForAppointment(appointment: BookableOfferingAppointment): OfferingBooking[] {
    return appointment.bookings.filter((booking) => booking.createdAt && !booking.deletedAt);
  }

  appointmentHasActiveBookings(appointment: BookableOfferingAppointment): boolean {
    return this.getActiveBookingsForAppointment(appointment).length > 0;
  }

  getMemberPricing(offeringAppointment: BookableOfferingAppointment, memberId: number): OfferingPricing | null {
    return offeringAppointment.memberPrices.find((memberPrice) => memberPrice.memberId === memberId);
  }

  appointmentIsCompletelyCanceled(appointment: BookableOfferingAppointment): boolean {
    if (this.appointmentHasActiveBookings(appointment)) {
      return false;
    }

    if (this.getCanceledBookingsForAppointment(appointment).length === 0) {
      return false;
    }

    return true;
  }

  getBookingStateForAppointmentAndMemberId(appointment: BookableOfferingAppointment, memberId: number): BookingState {
    const bookingForMember = this.getBookingForMemberId(appointment, memberId);
    const waitListEntryForMember = this.getWaitListEntryForMemberId(appointment, memberId);
    if (bookingForMember && bookingForMember.createdAt !== null && bookingForMember.deletedAt === null) {
      return BookingState.booked;
    } else if (bookingForMember && bookingForMember.createdAt !== null && bookingForMember.deletedAt !== null) {
      return BookingState.canceled;
    } else if (appointment.waitListActive && waitListEntryForMember) {
      if (this.waitListEntryIsCurrentlyBookable(waitListEntryForMember)) {
        return BookingState.waitListCurrentlyBookable;
      } else {
        return BookingState.waitListWaiting;
      }
    }

    return BookingState.open;
  }

  private getBookingForMemberId(appointment: BookableOfferingAppointment, memberId: number): OfferingBooking | undefined {
    return appointment.bookings?.find((booking) => booking.member.id === memberId);
  }

  private getWaitListEntryForMemberId(appointment: BookableOfferingAppointment, memberId: number): WaitListEntry | undefined {
    return appointment.waitListEntries?.find((waitListEntry) => waitListEntry.member.id === memberId);
  }

  private getCanceledBookingsForAppointment(appointment: BookableOfferingAppointment): OfferingBooking[] {
    return appointment.bookings.filter((booking) => !!booking.deletedAt);
  }

  getWaitListEntries(): Observable<WaitListEntry[]> {
    return this.refreshWaitListEntries$.pipe(
      startWith(null),
      switchMap(() => this.httpClient.get<DataResult<WaitListEntry[]>>(`${this.configService.config.apiUrl}/offering-waitlist`)),
      catchError(this.httpErrorHandler.handleErrorCallback),
      mapDataResult$<WaitListEntry[]>(),
    );
  }
}
